import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { batch, connect } from 'react-redux';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Icon from '@material-ui/core/Icon';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import Checkbox from '@material-ui/core/Checkbox';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
// Components
import FormSubmit from '../form/formSubmit';
import ErrorMessage from '../error-message';
import MainLoading from '../main-loading/global-loader';
import EnhancedTableHead from './table-header';
import TablePaginationActions from './table-pagination';
import TableRowsRender from './table-row';
import EnhancedTableToolbar from './table-toolbar';
import { TooltipButton } from '../tooltip-button';
// Modules
import { ListingMeta, SortOrder } from '../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../redux/reducers/pendingOperations';
import {
    actions as tableActions,
    makeMemoizedGetDatas,
    makeMemoizedGetFilters,
    makeMemoizedGetMetas,
    API_ENDPOINT,
    TableRequestPoint,
} from '../../redux/reducers/table';
// Ressources
import StringUtils from '../../utils/stringUtils';
import UserUtils, { LOCAL_STORAGE_TABLE_DENSE } from '../../utils/userUtils';
import { exportTableDatas } from '../../utils/tableExportUtils';
import { ROW_NUMBER_OPTIONS } from '../../utils/constants';
import { FormatType, HeadCell, IntervalKeys } from '../../local-types';
import { useTableState } from './hooks/useTableState';

interface TableOwnProps {
    headers: HeadCell[];
    type: TableRequestPoint;
    add?: string; // Path used when creating a new element
    onRef?: any; // Allow to access to inside fonctions
    actions?: JSX.Element | JSX.Element[];
    title?: string;
    filterParams?: { [key: string]: any };
    contentProps?: React.HTMLAttributes<HTMLDivElement>;
    filterVisible?: boolean;
    disableGoToLast?: boolean;
    tableConstantSize?: boolean;
    customRowPerPage?: number[];
    noPage?: boolean;
    onExport?: (startDate, endDate) => void;
}

interface TableReduxProps extends WithTranslation {
    datas: Array<any>;
    metas: ListingMeta;
    filters: any;
    isLoadingGetPages: boolean;
    getPages: (
        page: number,
        type: TableRequestPoint,
        sort: { property: string; nextOrder: SortOrder },
        filters: any,
        rowsPerPage?: number,
    ) => void;
    getPageError: ErrorPayload;
}

type TableProps = TableOwnProps & TableReduxProps;

const EnhancedTable = (props: TableProps): JSX.Element => {
    const {
        t,
        title,
        actions,
        contentProps,
        getPages,
        disableGoToLast,
        headers,
        datas,
        metas,
        filters,
        isLoadingGetPages,
        tableConstantSize,
        type,
        filterParams,
        filterVisible = true,
        customRowPerPage,
        noPage = false,
        getPageError,
        onRef,
        onExport,
    } = props;
    // console.log('Les filtrreeeesss', filters);
    const { order: urlOrder, page: urlPage, sort: urlSort, ...urlFilters } = filters;
    const headersIds = headers.map((h) => h.id);
    const queryIds = type.filters ? Object.keys(type.filters).map((h) => h) : [];
    const savedFixedFilter = Object.keys(urlFilters)
        .filter((key) => !queryIds.includes(key) && !headersIds.includes(/.*\[(.*)\]/g.exec(key)?.[1] || key))
        .reduce((obj, key) => {
            if (urlFilters[key]) {
                obj[key] = urlFilters[key];
            }
            return obj;
        }, {});

    const [uuidTable] = React.useState<string>(StringUtils.uuid());
    const [dateFilter, setDateFilter] = React.useState<string>(null);
    const [dateEndFilter, setDateEndFilter] = React.useState<string>(null);
    const [intervalLabel, setIntervalLabel] = React.useState<string>(null);
    const [intervalKeys, setIntervalKeys] = React.useState<IntervalKeys>(null);
    const [intervalDateTime, setIntervalDateTime] = React.useState<boolean>(false);
    const [barFilter, setBarFilter] = React.useState<boolean>(true);
    const [fixedFilter, setFixedFilter] = React.useState({ ...savedFixedFilter, ...filterParams });
    // Use this one if we don't want to mix saved filters and url filter
    const [filter, setFilter] = React.useState(
        Object.keys(filterParams || {}).length > 0 ? filterParams : { ...urlFilters, ...filterParams },
    );
    // const [filter, setFilter] = React.useState({ ...urlFilters, ...filterParams });

    const [tableColumns, setTableColumns] = React.useState(UserUtils.getTableColumnsForTable(window.location.pathname));
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const classes = useStyles();
    const currentProps = useRef(null);

    // Utiliser le hook personnalisé
    const { order, setOrder, orderBy, setOrderBy, page, setPage, dense, setDense, rowsPerPage, setRowsPerPage } =
        useTableState(filters, headers, customRowPerPage);

    //==========================================//
    //=============== UseEffect ================//
    //==========================================//

    useEffect(() => {
        if (props.onRef && currentProps?.current?.onRef !== props.onRef) {
            props.onRef({ reload: reloadTable });
        }
        return () => {
            if (props.onRef) {
                props.onRef(undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRef]);

    useEffect(() => {
        if (currentProps?.current?.filterParams && !isEqual(currentProps.current.filterParams, props.filterParams)) {
            currentProps.current = props;
            batch(() => {
                const newFilters = { ...urlFilters, ...filterParams };
                setFilter(newFilters);
                getPages(page, type, { property: orderBy, nextOrder: order }, newFilters, rowsPerPage);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterParams]);

    useEffect(() => {
        if (currentProps?.current?.type !== props.type) {
            currentProps.current = props;

            props.getPages(page, type, { property: orderBy, nextOrder: order }, filter, rowsPerPage);

            const interval = headers.filter((e) => e.interval === true);
            if (interval?.length > 0) {
                const cell = interval[0];
                setDateFilter(cell.id);
                setIntervalLabel(cell.label);
                setIntervalDateTime(cell.intervalDateTime);
                if (cell.intervalEnd) {
                    setDateEndFilter(cell.intervalEnd);
                }
                if (cell.intervalKeys) {
                    setIntervalKeys(cell.intervalKeys);
                }
            }

            const filterBar = headers.filter((e) => e.filterable === undefined || e.filterable === true);
            if (filterBar?.length === 0) {
                setBarFilter(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, headers]);

    //========================================//
    //=============== Actions ================//
    //========================================//

    const reloadTable = (): void => {
        getPages(page, type, { property: orderBy, nextOrder: order }, filter, rowsPerPage);
    };

    const handleSubmit = (e): void => {
        e.persist();
        e.preventDefault();
        e.stopPropagation();

        let fields = Array.prototype.slice
            .call(e.target)
            .filter((el) => el.name)
            .reduce((form, el) => {
                // Handle multiple select value gathering
                if (el.multiple && el.options?.length > 0) {
                    const values = [];
                    for (let i = 0; i < el.options.length; i++) {
                        const option = el.options[i];
                        // The empty option of a picker means "no filter", it must not be sent
                        if (option.selected && option.value) {
                            values.push(option.value);
                        }
                    }

                    if (values.length === 0) {
                        return form;
                    }

                    // The APIs read a multi valued filter differently: the core listing and the
                    // billing one expect an "in[field]" query, privapi a repeated "field[]"
                    // parameter - URLUtils.urlToFilter appends the brackets to an array value.
                    const multipleKey =
                        type?.type === API_ENDPOINT.PRIVAPI ? el.name.replace(/\[\]$/, '') : `in[${el.name}]`;

                    return {
                        ...form,
                        [multipleKey]: values,
                    };
                }

                const index = headers.findIndex((header) => header.id === el.name);
                let value = (el.alt || el.value)?.trim();

                // Phone Case
                const isPhone = el.placeholder === t('common:form.placeholder.phone');
                if (isPhone && value) {
                    // Normalize phone number - removing spaces
                    value = value.replace(/\s/g, '');
                }

                // Registration number case
                if (index !== -1 && headers[index].type === FormatType.Registration && value) {
                    // Normalize the plate to the SIV format AA-123-AA expected by the API
                    value = StringUtils.formatRegistrationNumber(value);
                }

                if (index !== -1 && headers[index].customValue) {
                    // console.log(index, headers[index]);
                    const custom = headers[index].customValue(value);
                    // console.log(custom, {
                    //     ...form,
                    //     ...custom,
                    // });
                    if (custom) {
                        return {
                            ...form,
                            ...custom,
                        };
                    }
                }

                if (
                    (index !== -1 &&
                        (headers[index].type === FormatType.DateLong ||
                            headers[index].type === FormatType.DateShort)) ||
                    ((el.name.startsWith('before[') ||
                        el.name.startsWith('after[') ||
                        el.name.endsWith('_min') ||
                        el.name.endsWith('_max')) &&
                        value)
                ) {
                    const match = /(\d{2})\/(\d{2})\/(\d{4})( (\d{2}):(\d{2}))?/g.exec(value);
                    value = null;
                    if (match) {
                        if (
                            !isNaN(+match[6]) &&
                            !isNaN(+match[5]) &&
                            !isNaN(+match[3]) &&
                            !isNaN(+match[2]) &&
                            !isNaN(+match[1])
                        ) {
                            value = `${+match[3]}-${+match[2]}-${+match[1]} ${+match[5]}:${+match[6]}:00`;
                        } else if (!isNaN(+match[3]) && !isNaN(+match[2]) && !isNaN(+match[1])) {
                            value = `${+match[3]}-${+match[2]}-${+match[1]}`;
                        }
                    }
                }

                if (
                    !type?.type &&
                    index !== -1 &&
                    (headers[index].contains === false ||
                        (headers[index].type && [FormatType.Picker, FormatType.Boolean].includes(headers[index].type)))
                ) {
                    return {
                        ...form,
                        [`filters[${el.name}]`]: value,
                    };
                } else if (
                    !type?.type &&
                    !el.name.startsWith('before[') &&
                    !el.name.startsWith('after[') &&
                    !el.name.startsWith('in[')
                ) {
                    return {
                        ...form,
                        [`search[${el.name}]`]: value,
                    };
                } else {
                    if (el.alt) {
                        return {
                            ...form,
                            [`${el.name}`]: value,
                            // [`${el.name}_name`]: el.value,
                        };
                    }
                    return {
                        ...form,
                        [`${el.name}`]: value,
                    };
                }
            }, {});

        // Add fixed filters params that are kept once set.
        // fields = Object.assign(fields, fixedFilter); // Priority for fixed filters
        fields = { ...fixedFilter, ...fields }; // Priority for new filters

        batch(() => {
            setFilter(fields);
            setPage(0);
            getPages(0, type, { property: orderBy, nextOrder: order }, fields, rowsPerPage);
        });
    };

    // Mémoriser les fonctions de callback
    const handleRequestSort = useCallback(
        (event: React.MouseEvent<unknown>, property: any) => {
            const isDesc = orderBy === property && order === SortOrder.DESC;
            const nextOrder = isDesc ? SortOrder.ASC : SortOrder.DESC;

            batch(() => {
                setOrder(nextOrder);
                setOrderBy(property);
                getPages(page, type, { property, nextOrder }, filter, rowsPerPage);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orderBy, order, page, type, filter, rowsPerPage],
    );

    const handleChangePage = useCallback(
        (event: unknown, newPage: number): void => {
            batch(() => {
                getPages(newPage, type, { property: orderBy, nextOrder: order }, filter, rowsPerPage);
                setPage(newPage);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [type, orderBy, order, filter, rowsPerPage],
    );

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        batch(() => {
            setRowsPerPage(newRowsPerPage);
            setPage(0);
            getPages(0, type, { property: orderBy, nextOrder: order }, filter, newRowsPerPage);
        });
    };

    const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>): void => {
        UserUtils.setPref(LOCAL_STORAGE_TABLE_DENSE, { activated: event.target.checked });
        setDense(event.target.checked);
    };

    const handleDelete =
        (chipkeyToDelete: any): (() => void) =>
        () => {
            const newFixedFilter = Object.assign({}, fixedFilter);
            const newFilter = Object.assign({}, filter);
            delete newFixedFilter[chipkeyToDelete];
            delete newFilter[chipkeyToDelete];
            batch(() => {
                setFixedFilter(newFixedFilter);
                setFilter(newFilter);
                getPages(page, type, { property: orderBy, nextOrder: order }, newFilter, rowsPerPage);
            });
        };

    const handleNoPropagation = (e): void => {
        e.stopPropagation();
        e.preventDefault();
    };

    //================================================//
    //=============== Computed Values ================//
    //================================================//

    const filteredHeaders = useMemo(
        () =>
            headers.filter((h) =>
                tableColumns?.[h.id] !== undefined
                    ? tableColumns?.[h.id]
                    : h.defaultVisibility !== undefined
                      ? h.defaultVisibility
                      : true,
            ),
        [headers, tableColumns],
    );

    const emptyRows = rowsPerPage - datas.length;

    const filterColumnsOn = filteredHeaders.length !== headers.length;

    const rowClass = type && type.handleRow ? classes.clickableRow : classes.row;

    const exportDisabled = isLoadingGetPages || !datas?.length;

    // Export the rows currently displayed: same page, same visible columns, same formatting
    const handleExportVisible = (): void => {
        exportTableDatas(datas, filteredHeaders, t, title || type?.url || window.location.pathname, filter);
    };

    //=======================================//
    //=============== Renders ===============//
    //=======================================//

    const renderTopBar = (): React.ReactNode => {
        if (!title && (isEmpty(fixedFilter) || !filterVisible) && !dateFilter && !actions && !props.add) {
            return null;
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 16 }}>
                {title && (
                    <div style={{ display: 'flex', paddingLeft: 24, alignSelf: 'center' }}>
                        {title && (
                            <Typography className={classes.title} variant="h6" id="tableTitle">
                                {title}
                            </Typography>
                        )}
                    </div>
                )}
                {filterVisible &&
                    fixedFilter &&
                    Object.keys(fixedFilter).filter((k) => !k.startsWith('before') && !k.startsWith('after')).length >
                        0 && (
                        <div style={{ display: 'flex', padding: 16, alignItems: 'center', color: 'darkgrey' }}>
                            {/* <b style={{ fontSize: 15 }}>{t('filter')}</b> */}
                            <Icon className={classes.icon}>filter_list</Icon>
                            {Object.keys(fixedFilter)
                                .filter((k) => !k.startsWith('before') && !k.startsWith('after'))
                                .map((key) => {
                                    const regex = /(.*?)(\[(.*?)\])(\[(.*?)\])?/;
                                    const match = key.match(regex);
                                    let label = `${key} : ${t(fixedFilter[key])}`;
                                    if (match) {
                                        const [, type, , cle] = key.match(regex);
                                        const formatted = t(type, { cle: t('common:table.' + cle) });
                                        label = `${formatted} : ${t(fixedFilter[key])}`;
                                    }
                                    return (
                                        <Chip
                                            key={key}
                                            icon={null}
                                            label={label}
                                            onDelete={handleDelete(key)}
                                            className={classes.chip}
                                        />
                                    );
                                })}
                        </div>
                    )}
                <EnhancedTableToolbar
                    t={t}
                    uuidTable={uuidTable}
                    filter={filter}
                    date={dateFilter}
                    dateEnd={dateEndFilter}
                    intervalLabel={intervalLabel}
                    intervalKeys={intervalKeys}
                    intervalDateTime={intervalDateTime}
                    actions={actions}
                    onExport={onExport}
                    {...(props as any)}
                />
            </div>
        );
    };

    const renderPageBar = (border = true): React.ReactNode => (
        <div
            style={{
                display: 'flex',
                border: 0,
                borderTop: 1,
                borderBottom: 1,
                borderStyle: 'solid',
                borderColor: border ? '#00000020' : 'transparent',
            }}
        >
            <div style={{ marginLeft: 20, display: 'flex', flex: 1, color: '#5d656b' }}>
                <FormControlLabel
                    control={<Switch checked={dense} onChange={handleChangeDense} />}
                    label={t('dense')}
                />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <span>{t('filterColumns')}</span>
                <TooltipButton title={t('filterColumnsAction')}>
                    <Button aria-describedby={id} variant="text" onClick={handleClick}>
                        <Icon color={filterColumnsOn ? 'secondary' : 'primary'}>dashboard</Icon>
                    </Button>
                </TooltipButton>
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <div className={classes.cells}>
                        {headers?.map((h) => {
                            const isChecked =
                                tableColumns?.[h.id] !== undefined
                                    ? tableColumns?.[h.id]
                                    : h.defaultVisibility !== undefined
                                      ? h.defaultVisibility
                                      : true;
                            return (
                                <div key={h.label}>
                                    <Checkbox
                                        checked={isChecked}
                                        value={h.id}
                                        disabled={isChecked && filteredHeaders.length === 1}
                                        onChange={(e) => {
                                            UserUtils.setTableColumns(window.location.pathname, h.id, !isChecked);
                                            setTableColumns(
                                                UserUtils.getTableColumnsForTable(window.location.pathname),
                                            );
                                        }}
                                    />
                                    {h.label}
                                </div>
                            );
                        })}
                    </div>
                </Popover>
            </div>
            {headers && headers.filter((d) => d.filterable || d.filterable === undefined).length > 0 && (
                <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                    <TooltipButton title={t('searchTooltipAction')}>
                        <Button
                            aria-label="Launch list Filter"
                            type="submit"
                            variant="text"
                            startIcon={<SearchIcon />}
                            form={uuidTable}
                        >
                            {t('searchTooltip')}
                        </Button>
                    </TooltipButton>
                </div>
            )}
            <div style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                <TooltipButton title={t(exportDisabled ? 'exportEmpty' : 'exportVisibleAction')}>
                    <Button
                        aria-label="Export displayed rows"
                        variant="text"
                        disabled={exportDisabled}
                        startIcon={<GetAppIcon />}
                        onClick={handleExportVisible}
                    >
                        {t('export')}
                    </Button>
                </TooltipButton>
            </div>
            <TablePagination
                style={{ color: '#5d656b' }}
                rowsPerPageOptions={noPage ? [datas?.length ?? 0] : (customRowPerPage ?? ROW_NUMBER_OPTIONS)}
                component="div"
                count={Number(metas.total)}
                labelRowsPerPage={t('rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                    isNaN(to) || isNaN(count)
                        ? `${t('common:loading')}`
                        : `${from ? from : 0}-${to} ${t('of')} ${count}`
                }
                rowsPerPage={noPage ? datas?.length : rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={(subProps) => (
                    <TablePaginationActions {...subProps} disableGoToLast={disableGoToLast} />
                )}
            />
        </div>
    );

    return (
        <div {...contentProps} className={classes.root}>
            <ErrorMessage payload={getPageError} />
            <React.Suspense fallback={<MainLoading center />}>
                <Paper className={classes.paper}>
                    <FormSubmit debounceSubmit={{ callback: handleSubmit }} id={uuidTable} stopPropagation={true}>
                        <input type="submit" hidden value="Ok" />
                    </FormSubmit>
                    {renderTopBar()}
                    {renderPageBar()}
                    <div className={classes.tableWrapper}>
                        <Table className={classes.table} size={dense ? 'small' : 'medium'} aria-label="Enhanced table">
                            <EnhancedTableHead
                                {...(props as any)}
                                t={t}
                                uuidTable={uuidTable}
                                headers={filteredHeaders}
                                type={type}
                                filter={filter}
                                barFilter={barFilter}
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                            />
                            <TableBody>
                                {!isLoadingGetPages && datas && metas.total !== undefined ? (
                                    <TableRowsRender
                                        datas={datas}
                                        headers={filteredHeaders}
                                        rowClass={rowClass}
                                        type={type}
                                        handleNoPropagation={handleNoPropagation}
                                    />
                                ) : (
                                    <TableRow tabIndex={-1} className={rowClass}>
                                        <TableCell colSpan={filteredHeaders.length}>
                                            <MainLoading center />
                                        </TableCell>
                                    </TableRow>
                                )}
                                {/* Automatically add empty rows to complete and have always same number of line in the table */}
                                {tableConstantSize && emptyRows > 0 && (
                                    <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                        <TableCell colSpan={6}></TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    {renderPageBar(false)}
                </Paper>
            </React.Suspense>
        </div>
    );
};

const makemapStateToProps = () => {
    const getDatas = makeMemoizedGetDatas();
    const getMetas = makeMemoizedGetMetas();
    const getFilters = makeMemoizedGetFilters();

    const mapStateToProps = (state, ownProps: TableOwnProps) => {
        return {
            datas: getDatas(state, ownProps),
            metas: getMetas(state, ownProps),
            filters: getFilters(state, ownProps),
            isLoadingGetPages: pendingSelectors.isLoadingGetPages(state),
            getPageError: errorSelectors.getPagesError(state),
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = {
    getPages: tableActions.getPages,
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            marginTop: theme.spacing(3),
            '& .MuiFormControl-root.MuiTextField-root': {
                '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl[data-shrink=true]': {
                    whiteSpace: 'nowrap',
                },
                '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl[data-shrink=false]': {
                    whiteSpace: 'nowrap',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    paddingRight: 16,
                },
            },
        },
        clickableRow: {
            '& .tableCell': {
                padding: 0,
                height: 1,
                '@media screen and (min--moz-device-pixel-ratio:0)': {
                    height: '100%',
                },
                '& a': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: theme.spacing(2),
                    height: '100%',
                },
                '& a.MuiLink-underlineHover:hover': {
                    textDecoration: 'none',
                },
                '&.MuiTableCell-sizeSmall a': {
                    padding: '6px 24px 6px 16px',
                },
            },
            '&.MuiTableRow-root.MuiTableRow-hover:hover': {
                backgroundColor: theme.palette.colors.table.highlight,
                '& b, & .MuiCircularProgress-root, & select, & .MuiSelect-icon, & .MuiChip-icon, & .MuiSvgIcon-root': {
                    color: theme.palette.colors.table.highlighted,
                },
                '& .MuiInput-underline:before': {
                    borderColor: theme.palette.colors.table.highlighted,
                },
                /*Add , & .chipInvalid, & .chipValid to change chips text color*/
                '& .clickableRow': {
                    color: theme.palette.colors.table.highlighted,
                    backgroundColor: theme.palette.colors.table.highlightTransparent,
                },
            },
            '&.MuiTableRow-root.MuiTableRow-hover .MuiChip-root': {
                transition: 'none',
            },
            '&.MuiTableRow-root.MuiTableRow-hover:hover td:first-child': {
                borderTopLeftRadius: 10,
                borderBottomLeftRadius: 10,
            },
            '&.MuiTableRow-root.MuiTableRow-hover:hover td:last-child': {
                borderTopRightRadius: 10,
                borderBottomRightRadius: 10,
            },
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.colors.table.hover,
            },
        },
        chip: {
            margin: theme.spacing(0.5),
            borderRadius: 4,
            fontWeight: 'bold',
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.secondary.main,
            '& .MuiSvgIcon-root': {
                color: theme.palette.primary.contrastText,
            },
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 300,
            borderCollapse: 'separate',
        },
        row: {
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.colors.table.hover,
            },
        },
        title: {
            flex: '1 1 100%',
        },
        icon: {
            borderStyle: 'solid',
            borderWidth: 1,
            borderRadius: 5,
            height: 'auto',
            width: 'auto',
        },
        tableWrapper: {
            overflowX: 'auto',
        },
        searchButton: {
            marginTop: 16,
            marginLeft: 16,
        },
        cells: {
            margin: 8,
            display: 'flex',
            flexDirection: 'row',
        },
    }),
);

const EnhancedTableWithTranslation = withTranslation(['table', 'booking', 'common'])(EnhancedTable);

export default React.memo(connect(makemapStateToProps, mapDispatchToProps)(EnhancedTableWithTranslation));
